import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  RadialLinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  LineElement,
  PointElement,
  Filler,
} from "chart.js";
import { Bar, Radar } from "react-chartjs-2";
import { baseUrl } from "../app/api/apiSlice";
import {
  Container,
  Header,
  Grid,
  Icon,
  Loader,
  Dimmer,
  Table,
  Progress,
  Segment,
  Tab,
} from "semantic-ui-react";
import CustomDropdown from "./CustomDropdown";
import { PAGE_SIZE } from "../constants";
import "./LandingPage.css";

ChartJS.register(
  CategoryScale,
  LinearScale,
  RadialLinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  LineElement,
  PointElement,
  Filler
);

export const RANGE_OPTIONS = [
  { key: "1d", text: "1일", value: "1d" },
  { key: "1w", text: "1주", value: "1w" },
  { key: "1m", text: "1개월", value: "1m" },
  { key: "6m", text: "6개월", value: "6m" },
];

const VALID_RANGES = RANGE_OPTIONS.map((o) => o.value);

export function resolveInitialRange(search) {
  const value = new URLSearchParams(search).get("range");
  return VALID_RANGES.includes(value) ? value : "1m";
}

const StaffHome = ({ user, navigate }) => {
  const token = useSelector((state) => state.auth.token);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  // Contractor Filter State (staff only)
  const [contractors, setContractors] = useState([]);
  const [selectedContractor, setSelectedContractor] = useState(null);

  const [range, setRange] = useState(() =>
    resolveInitialRange(window.location.search)
  );

  // 700, not the app's usual 768: this breakpoint is measured, not inherited. The title
  // block is ~165px, the pills ~206px and the dropdown 180px, so one row needs ~575px of
  // container and fits down to roughly a 610px viewport. Stacking at 768 stretched the
  // segmented control to 723px on a tablet that had room to spare. 700 leaves headroom
  // for a longer 이름/파트너 widening the title, and the outer flexWrap below is the
  // backstop if one ever does.
  const [isNarrow, setIsNarrow] = useState(window.innerWidth <= 700);
  useEffect(() => {
    const onResize = () => setIsNarrow(window.innerWidth <= 700);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const handleRangeChange = (value) => {
    setRange(value);
    const params = new URLSearchParams(window.location.search);
    params.set("range", value);
    window.history.replaceState(
      null,
      "",
      `${window.location.pathname}?${params.toString()}`
    );
  };

  useEffect(() => {
    if (!user.is_staff) return;
    axios
      .get(`${baseUrl}/api/v1/accounts/contractors/`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        const results = res.data.results || res.data;
        const formatted = (Array.isArray(results) ? results : []).map((c) => ({
          key: c.id,
          text: c.name,
          value: c.id,
        }));
        setContractors(formatted);
      })
      .catch((err) => console.error("Failed to fetch contractors", err));
  }, [token, user.is_staff]);

  useEffect(() => {
    const fetchStats = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        params.set("range", range);
        if (selectedContractor) {
          params.set("contractor_id", selectedContractor);
        }
        const url = `${baseUrl}/api/v1/dashboard/stats/?${params.toString()}`;
        const response = await axios.get(url, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setStats(response.data);
      } catch (error) {
        console.error("Failed to fetch dashboard stats", error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [token, selectedContractor, range]);

  // --- CHART OPTIONS ---
  const commonOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: "rgba(0,0,0,0.8)",
        padding: 10,
        cornerRadius: 8,
        titleFont: {
          family: "-apple-system, BlinkMacSystemFont, sans-serif",
          size: 13,
        },
        bodyFont: {
          family: "-apple-system, BlinkMacSystemFont, sans-serif",
          size: 12,
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: {
          font: { family: "-apple-system, BlinkMacSystemFont, sans-serif" },
        },
      },
      y: {
        border: { display: false },
        grid: { color: "#f0f0f0" },
        beginAtZero: true,
        ticks: {
          font: { family: "-apple-system, BlinkMacSystemFont, sans-serif" },
        },
      },
    },
  };

  const radarOptions = {
    ...commonOptions,
    scales: {
      r: {
        angleLines: { display: true, color: "#f0f0f0" },
        grid: { color: "#f0f0f0" },
        pointLabels: {
          font: {
            family: "-apple-system, BlinkMacSystemFont, sans-serif",
            size: 11,
            weight: "600",
          },
          color: "#86868b",
        },
        suggestedMin: 0,
        suggestedMax: 100,
        ticks: { display: false }, // Hide numbers on radar rings for cleaner look
      },
    },
  };

  const SummarySection = () => (
    <Grid stackable columns={4} style={{ marginBottom: "20px" }}>
      {[
        {
          label: "총 교육생",
          value: stats.counts.trainees,
          icon: "users",
          color: "#007AFF",
        },
        {
          label: "총 응대 세션",
          value: stats.counts.sessions,
          icon: "comments",
          color: "#5856D6",
        },
        {
          label: "총 퀴즈 풀이",
          value: stats.counts.quiz_attempts,
          icon: "pencil alternate",
          color: "#FF9500",
        },
        {
          label: "참여 파트너",
          value: stats.contractor_stats.length,
          icon: "building",
          color: "#8E8E93",
        },
      ].map((item, idx) => (
        <Grid.Column key={idx}>
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "16px",
              padding: "24px",
              boxShadow: "0 2px 10px rgba(0,0,0,0.03)",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <div>
              <div
                style={{
                  fontSize: "13px",
                  color: "#86868b",
                  fontWeight: "500",
                  marginBottom: "4px",
                }}
              >
                {item.label}
              </div>
              <div
                style={{
                  fontSize: "28px",
                  fontWeight: "700",
                  color: "#1d1d1f",
                }}
              >
                {item.value}
              </div>
            </div>
            <div
              style={{
                width: "48px",
                height: "48px",
                borderRadius: "12px",
                backgroundColor: `${item.color}15`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Icon
                name={item.icon}
                style={{ fontSize: "20px", color: item.color, margin: 0 }}
              />
            </div>
          </div>
        </Grid.Column>
      ))}
    </Grid>
  );

  const ResponseSkillsTab = () => (
    <div>
      {/* Response Skills KPI Row */}
      <Grid stackable columns={4} style={{ marginBottom: "20px", marginTop: "20px" }}>
        {[
          { label: "평가 완료", value: stats.counts.evaluations, icon: "clipboard check", color: "#30B0C7" },
          { label: "평균 점수", value: stats.counts.average_score, icon: "star", color: "#34C759" },
          {
            // Threshold comes from the API (PASS_SCORE_PERCENT) rather than being written
            // here. The old label said "80점+" while the backend compared raw points to 80
            // against a 95-point maximum, so it read as a percentage and was not one.
            label: `Pass 비율 (달성률 ${stats.counts.pass_threshold_percent ?? 80}%+)`,
            value: `${stats.counts.pass_rate}%`,
            icon: "check circle",
            color: "#007AFF",
          },
          { 
            label: "평균 기준 충족률", 
            value: `${Math.round(stats.charts.radar_chart.data.reduce((a, b) => a + b, 0) / (stats.charts.radar_chart.data.length || 1))}%`, 
            icon: "chart pie", 
            color: "#5856D6" 
          },
        ].map((item, idx) => (
          <Grid.Column key={idx}>
            <div style={{ 
              backgroundColor: 'white', 
              borderRadius: '16px', 
              padding: '24px', 
              boxShadow: '0 2px 10px rgba(0,0,0,0.03)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <div>
                <div style={{ fontSize: '13px', color: '#86868b', fontWeight: '500', marginBottom: '4px' }}>{item.label}</div>
                <div style={{ fontSize: '24px', fontWeight: '700', color: '#1d1d1f' }}>{item.value}</div>
              </div>
              <div style={{ 
                width: '42px', 
                height: '42px', 
                borderRadius: '10px', 
                backgroundColor: `${item.color}15`, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center' 
              }}>
                <Icon name={item.icon} style={{ fontSize: '18px', color: item.color, margin: 0 }} />
              </div>
            </div>
          </Grid.Column>
        ))}
      </Grid>

      {/* Section 2: Main Trends */}
      <Header as="h3" style={{ margin: "30px 0 20px", color: "#1d1d1f" }}>
        성과 분석
      </Header>
      <Grid stackable columns={2}>
        <Grid.Column>
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "16px",
              padding: "24px",
              boxShadow: "0 2px 10px rgba(0,0,0,0.03)",
              height: "100%",
            }}
          >
            <Header
              as="h4"
              style={{ marginBottom: "20px", color: "#1d1d1f" }}
            >
              최근 30일 세션 추이
            </Header>
            <div style={{ height: "300px" }}>
              <Bar
                data={{
                  labels: stats.charts.sessions_over_time.labels,
                  datasets: [
                    {
                      type: "line",
                      label: "평균 점수",
                      data: stats.charts.sessions_over_time.avg_scores,
                      borderColor: "#FF3B30",
                      borderWidth: 2,
                      pointRadius: 0,
                      tension: 0.4,
                      yAxisID: "y1",
                    },
                    {
                      type: "bar",
                      label: "세션 수",
                      data: stats.charts.sessions_over_time.data,
                      backgroundColor: "#007AFF",
                      borderRadius: 4,
                      barThickness: 8,
                      yAxisID: "y",
                    },
                  ],
                }}
                options={{
                  ...commonOptions,
                  plugins: {
                    ...commonOptions.plugins,
                    legend: {
                      display: true, // Enable legend for multi-axis chart
                      position: "top",
                      labels: {
                        font: {
                          family:
                            "-apple-system, BlinkMacSystemFont, sans-serif",
                          size: 11,
                        },
                        usePointStyle: true,
                        boxWidth: 6,
                      },
                    },
                  },
                  scales: {
                    ...commonOptions.scales,
                    y: {
                      display: true, // Enable y-axis
                      grid: { display: true, color: "#f0f0f0" }, // Enable grid lines
                      ticks: {
                        display: true,
                        font: {
                          family:
                            "-apple-system, BlinkMacSystemFont, sans-serif",
                        },
                      },
                      title: {
                        display: true,
                        text: "세션 수",
                        font: {
                          family:
                            "-apple-system, BlinkMacSystemFont, sans-serif",
                          size: 11,
                        },
                      },
                    },
                    y1: {
                      display: true,
                      position: "right",
                      min: 0,
                      max: 100,
                      grid: { display: false },
                      ticks: {
                        display: true,
                        font: {
                          family:
                            "-apple-system, BlinkMacSystemFont, sans-serif",
                        },
                      },
                      title: {
                        display: true,
                        text: "평균 점수",
                        font: {
                          family:
                            "-apple-system, BlinkMacSystemFont, sans-serif",
                          size: 11,
                        },
                      },
                    },
                  },
                }}
              />
            </div>
          </div>
        </Grid.Column>
        <Grid.Column>
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "16px",
              padding: "24px",
              boxShadow: "0 2px 10px rgba(0,0,0,0.03)",
              height: "100%",
            }}
          >
            <Header
              as="h4"
              style={{ marginBottom: "20px", color: "#1d1d1f" }}
            >
              영역별 평균 달성률
            </Header>
            <div
              style={{
                height: "300px",
                display: "flex",
                justifyContent: "center",
              }}
            >
              <Radar
                data={{
                  labels: stats.charts.radar_chart.labels,
                  datasets: [
                    {
                      label: "달성률",
                      data: stats.charts.radar_chart.data,
                      backgroundColor: "rgba(52, 199, 89, 0.2)",
                      borderColor: "#34C759",
                      borderWidth: 2,
                      pointRadius: 0,
                    },
                  ],
                }}
                options={radarOptions}
              />
            </div>
          </div>
        </Grid.Column>
      </Grid>

      {/* Section 3: Deep Dive */}
      <Grid stackable columns={2} style={{ marginTop: "10px" }}>
        <Grid.Column>
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "16px",
              padding: "24px",
              boxShadow: "0 2px 10px rgba(0,0,0,0.03)",
              height: "100%",
            }}
          >
            <Header
              as="h4"
              style={{ marginBottom: "20px", color: "#1d1d1f" }}
            >
              점수 분포
            </Header>
            <div style={{ height: "250px" }}>
              <Bar
                data={{
                  labels: stats.charts.score_distribution.labels,
                  datasets: [
                    {
                      data: stats.charts.score_distribution.data,
                      backgroundColor: "#007AFF",
                      borderRadius: 4,
                      barPercentage: 0.6,
                    },
                  ],
                }}
                options={{
                  ...commonOptions,
                  scales: {
                    ...commonOptions.scales,
                    x: {
                      ...commonOptions.scales.x,
                      title: {
                        display: true,
                        text: "점수 구간",
                        font: {
                          family:
                            "-apple-system, BlinkMacSystemFont, sans-serif",
                          size: 11,
                        },
                      },
                    },
                    y: {
                      ...commonOptions.scales.y,
                      title: {
                        display: true,
                        text: "세션 수",
                        font: {
                          family:
                            "-apple-system, BlinkMacSystemFont, sans-serif",
                          size: 11,
                        },
                      },
                    },
                  },
                }}
              />
            </div>
          </div>
        </Grid.Column>
        <Grid.Column>
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "16px",
              padding: "24px",
              boxShadow: "0 2px 10px rgba(0,0,0,0.03)",
              height: "100%",
            }}
          >
            <Header
              as="h4"
              style={{ marginBottom: "20px", color: "#1d1d1f" }}
            >
              어려워하는 페르소나 (하위 5)
            </Header>
            <div style={{ height: "250px" }}>
              {/* An empty axis reads as broken. This happens for a contractor with no
                  evaluations yet, for a short date range, and on demo data — whose sessions
                  were all imported from Apple's ACE files and therefore use placeholder
                  (is_sentinel) personas, which are excluded because a trainee cannot pick
                  them and so "personas trainees find hard" cannot mean anything for them. */}
              {(stats.charts.persona_distribution.data || []).length === 0 ? (
                <div
                  style={{
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#86868b",
                    fontSize: "13px",
                    textAlign: "center",
                    lineHeight: 1.6,
                  }}
                >
                  선택한 기간에 평가된 페르소나가 없습니다.
                </div>
              ) : (
              <Bar
                data={{
                  // The session count goes in the axis label rather than the tooltip: it is
                  // the whole reason this ranking is readable. A persona seen once used to
                  // sit beside one seen fifty times looking equally hard, and a count you
                  // have to hover to discover does not fix that.
                  labels: (stats.charts.persona_distribution.personas || []).length
                    ? stats.charts.persona_distribution.personas.map(
                        (p) => `${p.label} · ${p.count}회`
                      )
                    : stats.charts.persona_distribution.labels,
                  datasets: [
                    {
                      data: stats.charts.persona_distribution.data,
                      backgroundColor: "#FF3B30",
                      borderRadius: 4,
                      barThickness: 16,
                    },
                  ],
                }}
                options={{
                  ...commonOptions,
                  indexAxis: "y",
                  plugins: {
                    ...commonOptions.plugins,
                    tooltip: {
                      ...commonOptions.plugins.tooltip,
                      callbacks: {
                        // Who the persona is, for a manager deciding what to coach. The bar
                        // itself only carries a number.
                        title: (items) => {
                          const p =
                            stats.charts.persona_distribution.personas?.[
                              items[0].dataIndex
                            ];
                          if (!p) return items[0].label;
                          return [p.label, p.title].filter(Boolean).join(" · ");
                        },
                        label: (item) => {
                          const p =
                            stats.charts.persona_distribution.personas?.[
                              item.dataIndex
                            ];
                          if (!p) return `평균 달성률 ${item.parsed.x}%`;
                          const lines = [
                            `평균 달성률 ${p.avg_percent}% (${p.avg_score}점)`,
                            `평가 ${p.count}회`,
                          ];
                          if (p.age) lines.push(`${p.age}세`);
                          if (p.purpose_of_visit) {
                            lines.push(String(p.purpose_of_visit).slice(0, 40));
                          }
                          return lines;
                        },
                      },
                    },
                  },
                  scales: {
                    x: {
                      display: true,
                      max: 100,
                      title: {
                        display: true,
                        // Achievement %, not raw points: score_obtained is out of each
                        // group's own maximum (95 for ACE 2026), so plotting it against a
                        // 0-100 axis labelled 평균 점수 read ~4 points low.
                        text: "평균 달성률 (%)",
                        font: {
                          family:
                            "-apple-system, BlinkMacSystemFont, sans-serif",
                          size: 11,
                        },
                      },
                    },
                    y: { grid: { display: false } },
                  },
                }}
              />
              )}
            </div>
          </div>
        </Grid.Column>
      </Grid>

      {/* Section 4: Leaderboards */}
      <Header as="h3" style={{ margin: "40px 0 20px", color: "#1d1d1f" }}>
        교육생 현황
      </Header>
      <Grid stackable columns={2}>
        <Grid.Column>
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "16px",
              padding: "24px",
              boxShadow: "0 2px 10px rgba(0,0,0,0.03)",
            }}
          >
            <Header
              as="h4"
              style={{
                marginBottom: "20px",
                color: "#1d1d1f",
                display: "flex",
                alignItems: "center",
              }}
            >
              <Icon
                name="trophy"
                style={{ color: "#FFD60A", marginRight: "8px" }}
              />
              우수 교육생 (Top 5)
            </Header>
            <Table basic="very" compact style={{ border: "none" }}>
              <Table.Body>
                {stats.user_stats.top_performers.map((user, idx) => (
                  <Table.Row key={idx}>
                    <Table.Cell
                      width={1}
                      style={{ color: "#86868b", fontWeight: "bold" }}
                    >
                      {idx + 1}
                    </Table.Cell>
                    <Table.Cell>
                      <div
                        style={{
                          fontWeight: "600",
                          color: "#1d1d1f",
                          cursor: "pointer",
                        }}
                        onClick={() =>
                          navigate(`/userdashboard?userId=${user.id}`)
                        }
                      >
                        {user.name}
                      </div>
                    </Table.Cell>
                    <Table.Cell width={6}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <Progress
                          percent={user.avg_score}
                          size="tiny"
                          color="yellow"
                          style={{ margin: 0, flex: 1 }}
                        />
                        <span
                          style={{
                            fontSize: "13px",
                            fontWeight: "600",
                            color: "#1d1d1f",
                            width: "30px",
                            textAlign: "right",
                          }}
                        >
                          {user.avg_score}
                        </span>
                      </div>
                    </Table.Cell>
                    <Table.Cell
                      width={2}
                      textAlign="right"
                      style={{ color: "#86868b", fontSize: "13px" }}
                    >
                      {user.count} 세션
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </div>
        </Grid.Column>
        <Grid.Column>
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "16px",
              padding: "24px",
              boxShadow: "0 2px 10px rgba(0,0,0,0.03)",
            }}
          >
            <Header
              as="h4"
              style={{
                marginBottom: "20px",
                color: "#1d1d1f",
                display: "flex",
                alignItems: "center",
              }}
            >
              <Icon
                name="first aid"
                style={{ color: "#FF3B30", marginRight: "8px" }}
              />
              집중 케어 필요 (Bottom 5)
            </Header>
            <Table basic="very" compact style={{ border: "none" }}>
              <Table.Body>
                {stats.user_stats.low_performers.map((user, idx) => (
                  <Table.Row key={idx}>
                    <Table.Cell
                      width={1}
                      style={{ color: "#86868b", fontWeight: "bold" }}
                    >
                      {idx + 1}
                    </Table.Cell>
                    <Table.Cell>
                      <div
                        style={{
                          fontWeight: "600",
                          color: "#1d1d1f",
                          cursor: "pointer",
                        }}
                        onClick={() =>
                          navigate(`/userdashboard?userId=${user.id}`)
                        }
                      >
                        {user.name}
                      </div>
                    </Table.Cell>
                    <Table.Cell width={6}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <Progress
                          percent={user.avg_score}
                          size="tiny"
                          color="red"
                          style={{ margin: 0, flex: 1 }}
                        />
                        <span
                          style={{
                            fontSize: "13px",
                            fontWeight: "600",
                            color: "#1d1d1f",
                            width: "30px",
                            textAlign: "right",
                          }}
                        >
                          {user.avg_score}
                        </span>
                      </div>
                    </Table.Cell>
                    <Table.Cell
                      width={2}
                      textAlign="right"
                      style={{ color: "#86868b", fontSize: "13px" }}
                    >
                      {user.count} 세션
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </div>
        </Grid.Column>
      </Grid>

      {/* Section 5: Criteria Insights */}
      <Grid stackable columns={2} style={{ marginTop: "10px" }}>
        <Grid.Column>
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "16px",
              padding: "24px",
              boxShadow: "0 2px 10px rgba(0,0,0,0.03)",
              height: "100%",
            }}
          >
            <Header as="h4" style={{ marginBottom: "20px", color: "#34C759" }}>
              <Icon name="check circle" /> 가장 잘 지켜지는 항목
            </Header>
            {stats.criteria_stats.most_met.map((item, idx) => (
              <div
                key={idx}
                style={{
                  marginBottom: "16px",
                  paddingBottom: "16px",
                  borderBottom: idx < 4 ? "1px solid #f5f5f7" : "none",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    marginBottom: "6px",
                  }}
                >
                  <span
                    style={{
                      fontWeight: "600",
                      color: "#1d1d1f",
                      fontSize: "14px",
                      flex: 1,
                      marginRight: "10px",
                      wordBreak: "keep-all",
                    }}
                  >
                    {item.criterion__text}
                  </span>
                  <span
                    style={{
                      color: "#34C759",
                      fontWeight: "bold",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {item.count}회
                  </span>
                </div>
                {item.criterion__tags && (
                  <div style={{ fontSize: "12px", color: "#86868b" }}>
                    {item.criterion__tags
                      .split(",")
                      .map((t) => `#${t.trim()} `)}
                  </div>
                )}
              </div>
            ))}
          </div>
        </Grid.Column>
        <Grid.Column>
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "16px",
              padding: "24px",
              boxShadow: "0 2px 10px rgba(0,0,0,0.03)",
              height: "100%",
            }}
          >
            <Header as="h4" style={{ marginBottom: "20px", color: "#FF3B30" }}>
              <Icon name="exclamation circle" /> 가장 많이 놓치는 항목
            </Header>
            {stats.criteria_stats.most_missed.map((item, idx) => (
              <div
                key={idx}
                style={{
                  marginBottom: "16px",
                  paddingBottom: "16px",
                  borderBottom: idx < 4 ? "1px solid #f5f5f7" : "none",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    marginBottom: "6px",
                  }}
                >
                  <span
                    style={{
                      fontWeight: "600",
                      color: "#1d1d1f",
                      fontSize: "14px",
                      flex: 1,
                      marginRight: "10px",
                      wordBreak: "keep-all",
                    }}
                  >
                    {item.criterion__text}
                  </span>
                  <span
                    style={{
                      color: "#FF3B30",
                      fontWeight: "bold",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {item.count}회
                  </span>
                </div>
                {item.criterion__tags && (
                  <div style={{ fontSize: "12px", color: "#86868b" }}>
                    {item.criterion__tags
                      .split(",")
                      .map((t) => `#${t.trim()} `)}
                  </div>
                )}
              </div>
            ))}
          </div>
        </Grid.Column>
      </Grid>
    </div>
  );

  const ProductKnowledgeTab = () => (
    <div>
      {/* PK Summary Cards */}
      <Grid stackable columns={5} style={{ marginBottom: "20px", marginTop: "20px" }}>
        {[
          { label: "지식 평가 완료", value: stats.counts.pk_evaluations, icon: "clipboard list", color: "#FF9500" },
          { label: "평균 지식 점수", value: stats.counts.pk_average_score, icon: "lightbulb", color: "#FFD60A" },
          { label: "퀴즈 풀이 수", value: stats.counts.quiz_attempts, icon: "pencil alternate", color: "#FF3B30" },
          // First answer per trainee×question (what they knew) vs latest (what they know now):
          // the gap is what practice changed. Neither counts repeats (#249).
          { label: "퀴즈 첫 시도 정답률", value: `${stats.counts.quiz_accuracy}%`, icon: "check square", color: "#34C759" },
          { label: "퀴즈 현재 정답률", value: `${stats.counts.quiz_latest_accuracy ?? stats.counts.quiz_accuracy}%`, icon: "chart line", color: "#007AFF" },
        ].map((item, idx) => (
          <Grid.Column key={idx}>
            <div style={{ 
              backgroundColor: 'white', 
              borderRadius: '16px', 
              padding: '24px', 
              boxShadow: '0 2px 10px rgba(0,0,0,0.03)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <div>
                <div style={{ fontSize: '13px', color: '#86868b', fontWeight: '500', marginBottom: '4px' }}>{item.label}</div>
                <div style={{ fontSize: '24px', fontWeight: '700', color: '#1d1d1f' }}>{item.value}</div>
              </div>
              <div style={{ 
                width: '42px', 
                height: '42px', 
                borderRadius: '10px', 
                backgroundColor: `${item.color}15`, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center' 
              }}>
                <Icon name={item.icon} style={{ fontSize: '18px', color: item.color, margin: 0 }} />
              </div>
            </div>
          </Grid.Column>
        ))}
      </Grid>

      {/* PK Charts */}
      <Grid stackable columns={2}>
        <Grid.Column width={10}>
          <div style={{ backgroundColor: 'white', borderRadius: '16px', padding: '24px', boxShadow: '0 2px 10px rgba(0,0,0,0.03)', height: '100%' }}>
            <Header as="h4" style={{ marginBottom: '20px', color: '#1d1d1f' }}>제품별 지식 평가 점수 (Top 10)</Header>
            <div style={{ height: '300px' }}>
              <Bar 
                data={{
                  labels: stats.charts.pk_by_product.labels,
                  datasets: [{
                    label: '평균 점수',
                    data: stats.charts.pk_by_product.data,
                    backgroundColor: '#FF9500',
                    borderRadius: 4,
                    barThickness: 20
                  }]
                }}
                options={{
                  ...commonOptions,
                  scales: {
                    ...commonOptions.scales,
                    x: {
                      ...commonOptions.scales.x,
                      ticks: {
                        autoSkip: false,
                        maxRotation: 45,
                        minRotation: 45
                      }
                    }
                  }
                }}
              />
            </div>
          </div>
        </Grid.Column>
        <Grid.Column width={6}>
          <div style={{ backgroundColor: 'white', borderRadius: '16px', padding: '24px', boxShadow: '0 2px 10px rgba(0,0,0,0.03)', height: '100%' }}>
            <Header as="h4" style={{ marginBottom: '20px', color: '#1d1d1f' }}>제품별 퀴즈 정답률</Header>
            <div style={{ height: '300px' }}>
              <Bar 
                data={{
                  labels: stats.charts.quiz_by_product.labels,
                  datasets: [{
                    label: '정답률 (%)',
                    data: stats.charts.quiz_by_product.data,
                    backgroundColor: '#34C759',
                    borderRadius: 4,
                    barThickness: 16
                  }]
                }}
                options={{
                  ...commonOptions,
                  indexAxis: 'y',
                  scales: {
                    x: { display: true, max: 100, title: { display: true, text: '정답률 (%)' } },
                    y: { grid: { display: false } }
                  }
                }}
              />
            </div>
          </div>
        </Grid.Column>
      </Grid>

      <Grid stackable columns={1} style={{ marginTop: '10px' }}>
        <Grid.Column>
          <div style={{ backgroundColor: 'white', borderRadius: '16px', padding: '24px', boxShadow: '0 2px 10px rgba(0,0,0,0.03)' }}>
            <Header as="h4" style={{ marginBottom: '20px', color: '#1d1d1f' }}>최근 30일 지식 평가 점수 추이</Header>
            <div style={{ height: '250px' }}>
              <Bar 
                data={{
                  labels: stats.charts.pk_trend.labels,
                  datasets: [{
                    type: 'line',
                    label: '평균 점수',
                    data: stats.charts.pk_trend.data,
                    borderColor: '#FFD60A',
                    backgroundColor: 'rgba(255, 214, 10, 0.1)',
                    borderWidth: 2,
                    fill: true,
                    tension: 0.4,
                    pointRadius: 3
                  }]
                }}
                options={{
                  ...commonOptions,
                  scales: {
                    ...commonOptions.scales,
                    y: { 
                        display: true, 
                        min: 0, 
                        max: 100,
                        grid: { color: '#f0f0f0' }
                    }
                  }
                }}
              />
            </div>
          </div>
        </Grid.Column>
      </Grid>
    </div>
  );

  const panes = [
    {
      menuItem: '응대 스킬',
      render: () => <Tab.Pane attached={false} style={{ padding: 0, background: 'transparent', border: 'none', boxShadow: 'none' }}><ResponseSkillsTab /></Tab.Pane>,
    },
    {
      menuItem: '제품 지식',
      render: () => <Tab.Pane attached={false} style={{ padding: 0, background: 'transparent', border: 'none', boxShadow: 'none' }}><ProductKnowledgeTab /></Tab.Pane>,
    },
  ];

  return (
    <div
      style={{
        backgroundColor: "#f9fafb",
        minHeight: "100vh",
        paddingBottom: "60px",
      }}
    >
      {/* Sticky Header */}
      <div
        style={{
          padding: "16px 0",
          backgroundColor: "rgba(255, 255, 255, 0.9)",
          backdropFilter: "blur(20px)",
          borderBottom: "1px solid rgba(0,0,0,0.05)",
          marginBottom: "30px",
          position: "sticky",
          top: "50px", // Adjusted to account for fixed Navbar height (approx 50px)
          zIndex: 90, // Lower than Navbar zIndex
        }}
      >
        <Container>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              // The title and the controls used to sit on one unwrappable row. The range
              // pills (~206px) plus the 파트너 dropdown (180px) alone exceed a phone's
              // width, so the row pushed the document to 456px against a 390px viewport —
              // which is a horizontal scroll, and on a pinch-zoomed phone it is what makes
              // the whole page jump. Wrapping costs a line of height and nothing else.
              flexWrap: "wrap",
              gap: "12px",
            }}
          >
            {/* minWidth:0 so a long 파트너 or user name shrinks instead of forcing the row
                wider than its parent — flex items refuse to shrink below content width
                otherwise, which is how overflow survives a flexWrap. */}
            <div style={{ display: "flex", alignItems: "center", gap: "12px", minWidth: 0 }}>
              <div
                style={{
                  width: "40px",
                  height: "40px",
                  borderRadius: "50%",
                  backgroundColor: "#007AFF",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  lineHeight: 1,
                }}
              >
                <Icon
                  name="chart pie"
                  style={{
                    margin: 0,
                    color: "#fff",
                    fontSize: "18px",
                    height: "18px",
                    width: "18px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                />
              </div>
              <div>
                <h2
                  style={{
                    fontSize: "17px",
                    fontWeight: "600",
                    margin: 0,
                    color: "#1d1d1f",
                  }}
                >
                  관리자 대시보드
                </h2>
                <p style={{ fontSize: "13px", color: "#86868b", margin: 0 }}>
                  {user?.full_name || user?.username}님, 환영합니다.
                </p>
              </div>
            </div>
            {/* Below 768px the controls take the whole width and become a proper
                segmented control — the pills divide the row evenly and the dropdown sits
                full-width beneath. They cannot share a line at that size (206px + 180px
                against ~362px of container), so rather than let them wrap raggedly they
                stretch, which reads as a deliberate mobile layout instead of a spill.
                Desktop is untouched: natural widths, both on the title's row. */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                flexWrap: "wrap",
                justifyContent: "flex-end",
                width: isNarrow ? "100%" : "auto",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  backgroundColor: "#F5F5F7",
                  borderRadius: "12px",
                  padding: "3px",
                  height: "40px",
                  width: isNarrow ? "100%" : "auto",
                }}
              >
                {RANGE_OPTIONS.map((opt) => {
                  const isActive = range === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => handleRangeChange(opt.value)}
                      style={{
                        border: "none",
                        outline: "none",
                        cursor: "pointer",
                        height: "34px",
                        // flex:1 only on mobile, so the four segments divide the row
                        // evenly; on desktop they keep their natural label widths.
                        flex: isNarrow ? 1 : "0 0 auto",
                        padding: isNarrow ? "0 4px" : "0 14px",
                        borderRadius: "9px",
                        fontSize: "13px",
                        fontFamily: "inherit",
                        fontWeight: isActive ? "600" : "500",
                        color: isActive ? "#1d1d1f" : "#86868b",
                        backgroundColor: isActive ? "#ffffff" : "transparent",
                        boxShadow: isActive ? "0 1px 3px rgba(0,0,0,0.08)" : "none",
                        transition: "background-color 0.2s, color 0.2s",
                      }}
                    >
                      {opt.text}
                    </button>
                  );
                })}
              </div>
              {user.is_staff ? (
                <CustomDropdown
                  placeholder="전체 파트너"
                  options={contractors}
                  value={selectedContractor}
                  onChange={(value) => setSelectedContractor(value)}
                  style={{ width: isNarrow ? "100%" : "180px", height: "40px" }}
                />
              ) : (
                <span style={{ fontSize: "14px", fontWeight: "600", color: "#1d1d1f" }}>
                  {user.contractor?.name}
                </span>
              )}
            </div>
          </div>
        </Container>
      </div>

      <Container>
        {loading ? (
          <div
            style={{
              height: "400px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Loader active inline="centered" size="large">
              데이터 분석 중...
            </Loader>
          </div>
        ) : stats && (
          <>
            {/* 1. Global Summary */}
            <Header as="h3" style={{ margin: "0 0 20px", color: "#1d1d1f" }}>
              전체 현황
            </Header>
            <SummarySection />

            {/* 2. Tabbed Detail View */}
            <Tab 
                menu={{ secondary: true, pointing: true }} 
                panes={panes} 
                style={{ marginTop: '30px' }}
            />

            {/* Section 6: Contractor Table (Global for now, kept outside tabs) */}
            {stats.contractor_stats && stats.contractor_stats.length > 0 && (
              <div style={{ marginTop: "60px" }}>
                <Header
                  as="h3"
                  style={{ marginBottom: "20px", color: "#1d1d1f" }}
                >
                  파트너 성과
                </Header>
                <div
                  style={{
                    backgroundColor: "white",
                    borderRadius: "16px",
                    padding: "24px",
                    boxShadow: "0 2px 10px rgba(0,0,0,0.03)",
                  }}
                >
                  <Table basic="very" style={{ border: "none" }}>
                    <Table.Header>
                      <Table.Row>
                        <Table.HeaderCell
                          style={{ color: "#86868b", fontSize: "13px" }}
                        >
                          업체명
                        </Table.HeaderCell>
                        <Table.HeaderCell
                          style={{ color: "#86868b", fontSize: "13px" }}
                        >
                          평균 점수
                        </Table.HeaderCell>
                        <Table.HeaderCell
                          style={{ color: "#86868b", fontSize: "13px" }}
                        >
                          총 세션
                        </Table.HeaderCell>
                        <Table.HeaderCell
                          style={{ color: "#86868b", fontSize: "13px" }}
                        >
                          교육생 수
                        </Table.HeaderCell>
                      </Table.Row>
                    </Table.Header>
                    <Table.Body>
                      {stats.contractor_stats.map((c, idx) => (
                        <Table.Row key={idx}>
                          <Table.Cell
                            style={{ fontWeight: "600", color: "#1d1d1f" }}
                          >
                            {c.name}
                          </Table.Cell>
                          <Table.Cell>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "10px",
                              }}
                            >
                              <Progress
                                percent={c.avg_score}
                                size="tiny"
                                color="blue"
                                style={{ margin: 0, width: "100px" }}
                              />
                              <span style={{ fontWeight: "bold" }}>
                                {c.avg_score}
                              </span>
                            </div>
                          </Table.Cell>
                          <Table.Cell>{c.session_count}</Table.Cell>
                          <Table.Cell>{c.trainee_count}</Table.Cell>
                        </Table.Row>
                      ))}
                    </Table.Body>
                  </Table>
                </div>
              </div>
            )}
          </>
        )}
      </Container>
    </div>
  );
};

export default StaffHome;

